import { z } from 'zod';

const alphabetWithSpaces = z
  .string()
  .min(1, 'Field ini wajib diisi')
  .regex(/^[a-zA-Z\s]+$/, 'Hanya boleh huruf dan spasi');

const noSymbols = z
  .string()
  .min(1, 'Field ini wajib diisi')
  .regex(/^[^!@#$%^&*()+=[\]{};':"\\|,.<>?/~`]+$/, 'Tidak boleh mengandung simbol');

/**
 * Number inputs start out empty (''), not 0, so typing into a fresh field can
 * never produce a leading zero like "05". Both '' and undefined therefore have
 * to reach the wrapped schema as undefined, so the user sees the field's
 * required message instead of the "Harus berupa angka" type error.
 */
const requiredNumber = (schema: z.ZodType<number>) =>
  z.preprocess(val => (val === '' || val === null || Number.isNaN(val) ? undefined : val), schema);

const socialMediaMarketplaceSchema = z.object({
  social_media_marketplace: z.string().min(1, 'Akun sosial media/marketplace wajib diisi'),
  account_name: z.string().min(1, 'Nama akun sosial media/marketplace wajib diisi'),
});

/** Nilai harga terformat ("Rp500.000", "$1,000") → angka; pemisah ribuan dibuang. */
const priceToNumber = (price: string) => Number(price.replace(/\D/g, ''));

const WEBSITE_PATTERN = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(:\d+)?(\/\S*)?$/i;

const otherProductServiceSchema = z
  .object({
    product_service_name: z.string().optional().default(''),
    currency: z.enum(['Rp', '$']).optional().default('Rp'),
    estimated_price_min: z.string().optional().default(''),
    estimated_price_max: z.string().optional().default(''),
  })
  .superRefine((value, ctx) => {
    const hasName = value.product_service_name?.trim().length > 0;

    const hasMin = value.estimated_price_min?.trim().length > 0;

    const hasMax = value.estimated_price_max?.trim().length > 0;

    if (hasName && !hasMin && !hasMax) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['estimated_price_min'],
        message: 'Estimasi harga minimum wajib diisi',
      });

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['estimated_price_max'],
        message: 'Estimasi harga maksimum wajib diisi',
      });
    }

    if (hasName && hasMin && !hasMax) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['estimated_price_max'],
        message: 'Estimasi harga maksimum wajib diisi',
      });
    }

    if (hasName && !hasMin && hasMax) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['estimated_price_min'],
        message: 'Estimasi harga minimum wajib diisi',
      });
    }

    if (hasMin && hasMax && priceToNumber(value.estimated_price_max) < priceToNumber(value.estimated_price_min)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['estimated_price_max'],
        message: 'Harga maksimal tidak boleh lebih kecil dari harga minimal',
      });
    }
  });

const fixedOtherProductServicesSchema = z
  .array(otherProductServiceSchema)
  .length(5, 'Harus tersedia 5 slot kategori/layanan lainnya')
  .superRefine((rows, ctx) => {
    const firstRow = rows[0];
    const hasFirstName = firstRow?.product_service_name?.trim().length > 0;
    const hasFirstMin = firstRow?.estimated_price_min?.trim().length > 0;
    const hasFirstMax = firstRow?.estimated_price_max?.trim().length > 0;

    if (!hasFirstName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Kategori produk/layanan lainnya wajib diisi',
        path: [0, 'product_service_name'],
      });
    }

    if (!hasFirstMin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Estimasi harga minimum wajib diisi',
        path: [0, 'estimated_price_min'],
      });
    }

    if (!hasFirstMax) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Estimasi harga maksimum wajib diisi',
        path: [0, 'estimated_price_max'],
      });
    }
  });

/** Label tampilan status laporan keuangan PSAK; nilai tersimpan tetap "Sudah"/"Belum". */
export const financialReportLabel = (value: string) => ({ Sudah: 'Sudah Ada', Belum: 'Belum Ada' }[value] ?? value);

export const INDUSTRY_OPTIONS = [
  'Pendidikan',
  'FnB',
  'Manufaktur',
  'Retail & Toko',
  'Developer & Kontraktor',
  'Fashion',
  'Klinik',
  'Pariwisata',
  'Logistik',
  'Agro',
  'Teknik',
  'Lainnya',
] as const;

export const BUSINESS_CATEGORIES_BY_INDUSTRY = {
  Pendidikan: [
    'Sekolah Dasar (SD)',
    'Sekolah Menengah Pertama (SMP)',
    'Sekolah Menengah Atas (SMA)',
    'Perguruan Tinggi (Universitas / Institut / Politeknik)',
    'Pesantren',
    'Pendidikan Kesetaraan (Paket A/B/C)',
    'Lembaga Kursus dan Pelatihan (LKP/LPK)',
    'Pendidikan Keagamaan Non-Formal',
    'Pendidikan Non-Formal Lainnya',
    'Lainnya',
  ],
  FnB: ['Restoran', 'Warung Makan', 'Cafe', 'Lainnya'],
  Manufaktur: ['Makanan & Minuman', 'Otomotif', 'Farmasi', 'Tekstil', 'Beauty Manufaktur', 'Lainnya'],
  'Retail & Toko': [
    'Toko',
    'Toko dengan Ecommerce',
    'Retail',
    'Departemen Store',
    'Supermarket',
    'Toko Kelontong',
    'Indomaret',
    'Lainnya',
  ],
  'Developer & Kontraktor': ['Developer & Kontraktor', 'Lainnya'],
  Fashion: ['Perusahaan', 'Boutique', 'Lainnya'],
  Klinik: ['Klinik Kesehatan', 'Klinik Kecantikan', 'Lainnya'],
  Pariwisata: [
    'Akomodasi (Penginapan, Hotel, Villa, Glamping/Bumi Perkemahan)',
    'Transportasi (Pesawat, Kereta, Bus, Rental Mobil/Motor)',
    'Destinasi Wisata (Wisata Budaya & Sejarah, Wisata Alam, Taman Hiburan)',
    'Biro Perjalanan Wisata (Agen Tour/Travel Wisata, Haji, Umroh)',
    'Produk Kreatif (Aplikasi, Game, Musik, Film, Kriya, Souvenir)',
    'Penyelenggara Acara (Wedding, Pameran, Konferensi, Festival)',
    'Lainnya',
  ],
  Logistik: [
    'Pengiriman (Trucking, Air Freight, Sea Freight)',
    'Pergudangan (Warehousing, Cold Storage)',
    'Pengantaran (Kurir & Ekspedisi)',
    'Lainnya',
  ],
  Agro: ['Pertanian (Bibit, Pupuk, Pestisida)', 'Perkebunan', 'Peternakan', 'Perikanan', 'Lainnya'],
  Teknik: [
    'Perkapalan (Kapal, Perahu, Permesinan Marine, Perlengkapan Navigasi, Life Boat, Life Raft)',
    'Otomotif (Perakitan, Suku Cadang, Komponen Mekanik/Elektronik, Service Mobil/Motor)',
    'Konstruksi (EPC, Arsitek, Renovasi)',
    'Logam (Peleburan, Pemurnian, Pencetakan, Pengelasan)',
    'Non Logam (Semen, Pasir, Pencetakan, Batu)',
    'Energi (Batu Bara, Minyak, Gas, Mineral)',
    'Survey (Sertifikasi, Survey, Penelitian)',
    'Fabrikasi (Furnitur, Aluminium)',
    'Safety (APAR, APD, Safety Sign, HSE)',
    'Lainnya',
  ],
  Lainnya: ['Lainnya'],
} as const;

type IndustryOption = (typeof INDUSTRY_OPTIONS)[number];

export const baseCompanyDataSchema = z.object({
  start_date: z.string().optional(),
  end_date: z.string().optional(),

  pic_names: z.array(z.string()).optional(),

  client_name: alphabetWithSpaces.min(3, 'Nama klien minimal 3 karakter'),
  client_nickname: alphabetWithSpaces.min(3, 'Nama panggilan minimal 3 karakter'),
  owner_phone: z
    .string()
    .min(10, 'Nomor telepon minimal 10 digit')
    .max(15, 'Nomor telepon maksimal 15 digit')
    .regex(/^(\+62|0)\d{9,14}$/, 'Format nomor tidak valid (gunakan +62 atau 0)'),
  // Requiredness for the region fields below is conditional on citizenship,
  // so it lives in applyLocationRules() rather than on the field itself.
  owner_citizenship: z.enum(['WNI', 'WNA'], {
    errorMap: () => ({ message: 'Pilih kewarganegaraan pemilik' }),
  }),
  owner_foreign_domicile: z.string().optional(),
  province_id: z.string(),
  province_name: z.string(),
  regency_id: z.string(),
  regency_name: z.string(),
  owner_gender: z.enum(['pria', 'wanita'], {
    errorMap: () => ({ message: 'Pilih jenis kelamin' }),
  }),
  owner_religion: z.enum(['islam', 'kristen', 'katolik', 'hindu', 'buddha']).optional(),
  marital_status: z.enum(['belum menikah', 'menikah', 'Duda/Janda']).optional(),
  spouse_activity: z.string().optional(),
  self_description: z.string().optional(),

  // Komunitas Bisnis yang Diikuti (Opsional) — stored in business_condition_position on submit
  business_condition_position: z.string().optional(),
  business_communities: z.array(z.object({ name: z.string() })).optional(),

  business_name: noSymbols.min(3, 'Nama bisnis minimal 3 karakter'),
  business_legal_name: noSymbols.min(3, 'Nama legal bisnis minimal 3 karakter'),
  industry: z.string().min(1, 'Industri wajib dipilih'),
  business_category: z.string().min(1, 'Kategori bisnis wajib dipilih'),
  business_category_other: z.string().optional(),
  business_age_years: requiredNumber(
    z
      .number({ required_error: 'Lama usaha wajib diisi', invalid_type_error: 'Harus berupa angka' })
      .positive('Lama usaha harus angka positif')
      .int('Harus bilangan bulat'),
  ),
  branch_count: requiredNumber(
    z
      .number({ required_error: 'Jumlah cabang wajib diisi', invalid_type_error: 'Harus berupa angka' })
      .nonnegative('Jumlah cabang tidak boleh negatif')
      .int('Harus bilangan bulat'),
  ),

  ownership_status: z.enum(['Milik Pribadi', 'Syirkah'], {
    errorMap: () => ({ message: 'Pilih status kepemilikan' }),
  }),
  status_usaha: z.enum(['Pusat', 'Cabang'], {
    errorMap: () => ({ message: 'Pilih status usaha' }),
  }),
  partnership_count: z.preprocess(
    val => (val === '' || val === null || Number.isNaN(val) ? undefined : val),
    z.number().positive('Jumlah mitra harus positif').int('Harus bilangan bulat').optional(),
  ),
  legal_entity: z.enum(['PT', 'CV', 'UD', 'Perorangan', 'Yayasan'], {
    errorMap: () => ({ message: 'Pilih badan hukum' }),
  }),
  total_employees: requiredNumber(
    z
      .number({ required_error: 'Jumlah karyawan wajib diisi', invalid_type_error: 'Harus berupa angka' })
      .positive('Jumlah karyawan harus positif')
      .int('Harus bilangan bulat'),
  ),

  business_scale: z.string().min(1, 'Skala bisnis wajib diisi'),
  // Requiredness for the region fields below is conditional on whether the
  // business sits in Indonesia — see applyLocationRules().
  business_in_indonesia: z.enum(['Ya', 'Tidak'], {
    errorMap: () => ({ message: 'Pilih apakah usaha berada di wilayah Indonesia' }),
  }),
  business_foreign_location: z.string().optional(),
  business_province_id: z.string(),
  business_province_name: z.string(),
  business_regency_id: z.string(),
  business_regency_name: z.string(),
  office_phone: z.string().regex(/^\d*$/, 'Hanya boleh berisi angka'),
  business_website: z
    .string()
    .refine(value => value === '' || WEBSITE_PATTERN.test(value), 'Format website tidak valid, contoh: namabisnis.id'),
  head_office_address: z.string().min(1, 'Alamat kantor pusat wajib diisi'),
  has_financial_report: z.string().min(1, 'Field ini wajib diisi'),

  product_services_primary: z.string().min(1, 'Produk/layanan utama yang dijual wajib diisi'),
  social_media_marketplaces: z.array(socialMediaMarketplaceSchema).min(1, 'Minimal satu akun wajib diisi'),
  other_product_services: fixedOtherProductServicesSchema,

  main_problem_1: z.string().min(3, 'Masalah utama 1 wajib diisi'),
  main_problem_2: z.string().min(3, 'Masalah utama 2 wajib diisi'),
  main_problem_3: z.string().min(3, 'Masalah utama 3 wajib diisi'),
});

/**
 * Conditional location rules shared by the full schema and the per-step schemas.
 *
 * A WNI owner picks a province/regency; a WNA owner types a foreign domicile
 * instead. Same split for the business: inside Indonesia uses the region
 * cascade, outside uses free text. The field-level `min(1)` was removed from
 * those region fields so requiredness can depend on the flag — which means
 * every schema that validates them has to apply these rules, including the
 * `.pick()`-derived step schemas (a `.pick()` does not inherit refinements, and
 * the step schemas drive the wizard's completion checkmarks).
 */
type LocationRuleFields = {
  owner_citizenship?: 'WNI' | 'WNA';
  owner_foreign_domicile?: string;
  province_id?: string;
  regency_id?: string;
  business_in_indonesia?: 'Ya' | 'Tidak';
  business_foreign_location?: string;
  business_province_id?: string;
  business_regency_id?: string;
};

const addOwnerLocationIssues = (data: LocationRuleFields, ctx: z.RefinementCtx) => {
  if (data.owner_citizenship === 'WNA') {
    if (!data.owner_foreign_domicile?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Negara & kota domisili wajib diisi untuk WNA',
        path: ['owner_foreign_domicile'],
      });
    }
    return;
  }

  if (!data.province_id) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Provinsi wajib dipilih', path: ['province_id'] });
  }
  if (!data.regency_id) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Kota domisili wajib dipilih', path: ['regency_id'] });
  }
};

const addBusinessLocationIssues = (data: LocationRuleFields, ctx: z.RefinementCtx) => {
  if (data.business_in_indonesia === 'Tidak') {
    if (!data.business_foreign_location?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Negara & kota lokasi usaha wajib diisi',
        path: ['business_foreign_location'],
      });
    }
    return;
  }

  if (!data.business_province_id) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Provinsi lokasi usaha wajib dipilih',
      path: ['business_province_id'],
    });
  }
  if (!data.business_regency_id) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Kabupaten/Kota lokasi usaha wajib dipilih',
      path: ['business_regency_id'],
    });
  }
};

const addPartnershipIssues = (
  data: { ownership_status?: 'Milik Pribadi' | 'Syirkah'; partnership_count?: number },
  ctx: z.RefinementCtx,
) => {
  if (
    data.ownership_status === 'Syirkah' &&
    (typeof data.partnership_count !== 'number' || data.partnership_count <= 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Jumlah mitra wajib diisi jika status kepemilikan Syirkah',
      path: ['partnership_count'],
    });
  }
};

const addBusinessCategoryIssues = (
  data: { industry: string; business_category: string; business_category_other?: string },
  ctx: z.RefinementCtx,
) => {
  const validCategories = BUSINESS_CATEGORIES_BY_INDUSTRY[data.industry as IndustryOption];
  if (validCategories && !(validCategories as readonly string[]).includes(data.business_category)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Kategori bisnis tidak sesuai dengan industri yang dipilih',
      path: ['business_category'],
    });
  }
  if (
    (data.business_category === 'Pendidikan Non-Formal Lainnya' ||
      data.business_category === 'Lainnya' ||
      data.industry === 'Lainnya') &&
    !data.business_category_other?.trim()
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Keterangan Kategori Bisnis/Industri wajib diisi',
      path: ['business_category_other'],
    });
  }
};

const addStep2Issues = (
  data: LocationRuleFields &
    Parameters<typeof addPartnershipIssues>[0] &
    Parameters<typeof addBusinessCategoryIssues>[0],
  ctx: z.RefinementCtx,
) => {
  addBusinessCategoryIssues(data, ctx);
  addPartnershipIssues(data, ctx);
  addBusinessLocationIssues(data, ctx);
};

export const companyDataSchema = baseCompanyDataSchema.superRefine((data, ctx) => {
  addOwnerLocationIssues(data, ctx);
  addStep2Issues(data, ctx);
});

// z.input, not z.infer: the number fields hold '' while the user is editing and
// are only coerced to numbers on parse, so the form type must allow that.
export type CompanyDataFormValues = z.input<typeof baseCompanyDataSchema>;

export const step1Schema = baseCompanyDataSchema
  .pick({
    client_name: true,
    client_nickname: true,
    owner_phone: true,
    owner_citizenship: true,
    owner_foreign_domicile: true,
    province_id: true,
    province_name: true,
    regency_id: true,
    regency_name: true,
    owner_gender: true,
  })
  .superRefine(addOwnerLocationIssues);

export const step2Schema = baseCompanyDataSchema
  .pick({
    business_name: true,
    business_legal_name: true,
    industry: true,
    business_category: true,
    business_category_other: true,
    business_age_years: true,
    branch_count: true,
    ownership_status: true,
    status_usaha: true,
    partnership_count: true,
    legal_entity: true,
    total_employees: true,
    business_scale: true,
    business_in_indonesia: true,
    business_foreign_location: true,
    business_province_id: true,
    business_province_name: true,
    business_regency_id: true,
    business_regency_name: true,
    office_phone: true,
    business_website: true,
    head_office_address: true,
    has_financial_report: true,
    main_problem_1: true,
    main_problem_2: true,
    main_problem_3: true,
  })
  .superRefine(addStep2Issues);

/**
 * Aturan lintas-field tiap langkah, dijalankan tanpa mem-parse field dasar.
 * Zod melewati `superRefine` sebuah objek bila ada field yang gagal tipe (mis.
 * angka yang masih kosong), sehingga tanpa ini error lokasi/mitra baru muncul
 * setelah field lain beres dan pengguna harus menekan Lanjut dua kali.
 */
export const step1RulesSchema = z.custom<LocationRuleFields>().superRefine(addOwnerLocationIssues);
export const step2RulesSchema = z.custom<Parameters<typeof addStep2Issues>[0]>().superRefine(addStep2Issues);

export const step3Schema = baseCompanyDataSchema.pick({
  product_services_primary: true,
  social_media_marketplaces: true,
  other_product_services: true,
});
